/**
 * "My work" in the header — CRATUS 2.0 #19: the alarms assigned to the signed-in person that are
 * not finished, on every screen, and a toast the moment a new one is given to them.
 *
 * Beside the alarm bell, and a different question: the bell is everything that HAPPENED while you
 * watched; this is what is YOURS TO DO. It refreshes with every alarm query when the stream pushes
 * (`events.ts`), so an assignment made on another console arrives here within the burst.
 *
 * Hidden when there is nothing assigned and never has been in this visit — a control with nothing
 * in it is chrome — but stays once it has had something, so finishing the last item does not make
 * the place you were looking disappear under the cursor.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useToasts } from '@/app/toasts';
import { Icon } from '@/components/lumen/Icon';
import { useWorkNav } from '@/app/work-nav';
import { useAlarms } from '@/lib/api/queries';
import { useWorkOrders } from '@/lib/api/work';
import { dueText } from '@/modules/work/work-dates';
import type { AlarmRow } from '@/lib/api/types';
import { MY_WORK_LIFECYCLE, newAssignments, rankMyWork, workStateLabel } from '@/modules/alarms/my-work';

const since = (iso: string, now: number): string => {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m`;
  if (s < 172_800) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86_400)}d`;
};

export function MyWork({ onOpen }: { onOpen: (alarm: AlarmRow) => void }): ReactNode {
  const q = useAlarms({ state: 'all', assignee: 'me', lifecycle: MY_WORK_LIFECYCLE, limit: 100 });
  const rows = useMemo(() => rankMyWork(q.data?.data ?? []), [q.data]);
  /* Work orders assigned to me (Lumen-BE 0092), Cratus's: soonest due first, as the API sorts them. */
  const wq = useWorkOrders({ assignee: 'me', status: 'todo,in_progress', limit: 50 }, __PRODUCT__ === 'cratus');
  const work = useMemo(() => wq.data?.data ?? [], [wq.data]);
  const knownWork = useRef<Set<string> | null>(null);
  const [open, setOpen] = useState(false);
  const [ever, setEver] = useState(false);
  const known = useRef<Set<string> | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  /* Announce what is new since the last answer — nothing on the first. */
  useEffect(() => {
    if (!q.data) return;
    const fresh = newAssignments(known.current, q.data.data);
    for (const a of fresh.slice(0, 2)) {
      useToasts.getState().push('info', `Assigned to you: ${a.name ?? 'an alarm'}`, `${a.siteName} · ${a.deviceName}`);
    }
    known.current = new Set(q.data.data.map((a) => a.id));
    if (q.data.data.length > 0) setEver(true);
  }, [q.data]);

  useEffect(() => {
    if (!wq.data) return;
    if (knownWork.current !== null) {
      for (const w of wq.data.data.filter((x) => !knownWork.current?.has(x.id)).slice(0, 2)) {
        useToasts.getState().push('info', `Work assigned to you: ${w.ref}`, `${w.title} · ${w.site.name}`);
      }
    }
    knownWork.current = new Set(wq.data.data.map((w) => w.id));
    if (wq.data.data.length > 0) setEver(true);
  }, [wq.data]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!ever && rows.length === 0 && work.length === 0) return null;
  const waiting = rows.filter((a) => a.state === 'blocked' || a.state === 'verification_failed').length;
  const overdue = work.filter((w) => w.overdue).length;
  const total = rows.length + work.length;
  const label =
    total === 0
      ? 'Nothing assigned to you'
      : `${total} assigned to you${waiting > 0 ? `, ${waiting} waiting` : ''}${overdue > 0 ? `, ${overdue} overdue` : ''}`;
  const now = Date.now();

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="lm-hov relative flex items-center gap-[6px] rounded-[var(--radius-sm)] px-[8px]"
        style={{ height: 'var(--header-ctl-h)', color: total > 0 ? 'var(--fg)' : 'var(--muted-fg)' }}
        aria-label={label}
        title={label}
        aria-expanded={open}
      >
        <Icon name="ic-target" size={15} />
        <span className="hidden text-[12px] font-semibold md:inline">My work</span>
        {total > 0 && (
          <span
            className="lm-num flex min-w-[17px] items-center justify-center rounded-[var(--radius-pill)] px-[4px] text-[10px] font-bold"
            style={{ background: waiting > 0 || overdue > 0 ? 'var(--danger)' : 'var(--warn)', color: 'var(--primary-fg)' }}
          >
            {total}
          </span>
        )}
      </button>

      {open && (
        <div className="lm-overlay absolute right-0 top-[calc(100%+8px)] z-50 overflow-hidden" style={{ width: 360, maxWidth: 'calc(100vw - 24px)' }}>
          <div className="px-[13px] py-[9px] text-[12px] font-bold uppercase tracking-[0.08em]" style={{ borderBottom: '1px solid var(--glass-border)' }}>
            Assigned to you
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {total === 0 ? (
              <p className="px-[13px] py-[14px] text-[12px]" style={{ color: 'var(--muted-fg)' }}>
                Nothing is waiting on you. New assignments arrive here and as a notice.
              </p>
            ) : null}
            {work.length > 0 && (
              <>
                <div className="px-[13px] pb-[3px] pt-[8px] text-[10.5px] font-bold uppercase tracking-[0.08em]" style={{ color: 'var(--muted-fg)' }}>
                  Work orders
                </div>
                {work.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      useWorkNav.getState().open(w.id);
                    }}
                    className="lm-hov flex w-full items-start gap-[9px] px-[13px] py-[8px] text-left"
                    style={{ borderBottom: '1px solid var(--glass-border)' }}
                  >
                    <span className="mt-[4px] size-[7px] shrink-0 rounded-full" style={{ background: w.overdue ? 'var(--danger)' : w.status === 'in_progress' ? 'var(--warn)' : 'var(--muted-fg)' }} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12.5px] font-semibold">
                        {w.ref} · {w.title}
                      </span>
                      <span className="block truncate text-[11px]" style={{ color: 'var(--muted-fg)' }}>
                        {w.site.name}
                        {w.cabinet ? ` · ${w.cabinet.serialNumber}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-[11px]" style={{ color: w.overdue ? 'var(--danger-ink)' : 'var(--muted-fg)' }}>
                      {dueText(w, now)}
                    </span>
                  </button>
                ))}
              </>
            )}
            {rows.length > 0 && work.length > 0 && (
              <div className="px-[13px] pb-[3px] pt-[8px] text-[10.5px] font-bold uppercase tracking-[0.08em]" style={{ color: 'var(--muted-fg)' }}>
                Alarms
              </div>
            )}
            {rows.length === 0 ? null : (
              rows.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onOpen(a);
                  }}
                  className="lm-hov flex w-full items-start gap-[9px] px-[13px] py-[8px] text-left"
                  style={{ borderBottom: '1px solid var(--glass-border)' }}
                >
                  <span
                    className="mt-[4px] size-[7px] shrink-0 rounded-full"
                    style={{ background: a.state === 'blocked' || a.state === 'verification_failed' ? 'var(--danger)' : a.active ? 'var(--warn)' : 'var(--muted-fg)' }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-semibold">{a.name ?? 'Alarm'}</span>
                    <span className="block truncate text-[11px]" style={{ color: 'var(--muted-fg)' }}>
                      {a.siteName} · {a.deviceName} · {workStateLabel(a.state)}
                    </span>
                  </span>
                  <span className="lm-num shrink-0 text-[11px]" style={{ color: 'var(--muted-fg)' }}>
                    {since(a.openedAt, now)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
